import { useState, useRef, useEffect, memo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useWindowDimensions from "hooks/useWindowDimension";
import { useVisitorInfo, useLikedGifs } from "hooks";
import { IGif } from "services/types";
import AddToNichePopup from "../UI/Popups/AddToNichePopup";
import { useWindowSizes } from "hooks/useWindowSizes";
import { ReportPopup } from "../UI/Popups/Report";
import { GifPreferencePopup } from "components/UI/Popups/GifPreferencePopup";
import LiveButton from "components/ads/LiveButton";
import { LikeButton } from "./LikeButton";
import { SoundButton } from "./SoundButton";
import { apiMain } from "services";
import { AddToCollectionPopup } from "components/UI/Popups/Collections";
import { showAuthModal, setGifQuality, toggleGifSound } from "store/application/application-action";
import { PopOverMenu } from "components/UI/PopOverMenu";
import { ViewsButton, ViewsButtonType } from "components/buttons";
import { getMoreMenuData } from "./menus";
import { ReactComponent as MoreRoundIcon } from "../icons/moreRoundIcon.svg";
import { ReactComponent as FullScreenIcon } from "components/icons/fullScreenIcon.svg";
import { ReactComponent as SimpleScreenIcon } from "components/icons/simpleScreenIcon.svg";
import { ReactComponent as SDIcon } from "components/icons/sd.svg";
import { ReactComponent as HDIcon } from "components/icons/hd.svg";
import { EditNichesDialog } from "components/dialogs/EditNichesDialog";
import "./MoreButton.scss";
import { metrics } from "utils/metrics";
import "./styles/sidebar.scss";
import { ShareGifMenu } from "components/UI/ShareMenu/ShareGifMenu";

interface ISidebarProps {
  gif: IGif;
  embedded?: boolean;
  toggleFullScreen: () => void;
  hideGif: (id: string) => void;
  sound: boolean;
  quality: string;
  fullscreen: boolean;
  galleryGifId: null | string;
}

const CHANGE_MENU_ALIGN_WIDTH = 1140;

const SideBar = (props: ISidebarProps) => {
  const {
    gif,
    embedded,
    sound,
    toggleFullScreen,
    hideGif,
    quality,
    fullscreen,
    galleryGifId
  } = props;

  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
  const [isNicheOpen, setNicheOpen] = useState<boolean>(false);
  const [isBookmarkOpen, setIsBookmarkOpen] = useState<boolean>(false);
  const [isReportOpen, setReportOpen] = useState<boolean>(false);
  const [isGifPrefsOpen, setGifPrefsOpen] = useState<boolean>(false);
  const [liked, setLiked] = useState<boolean>(false);
  const [showShareMenu, setShowShareMenu] = useState<boolean>(false);
  const [showNichesDialog, setShowNichesDialog] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isDesktop = useWindowDimensions();
  const currentUser = useVisitorInfo();
  const dispatch = useDispatch();
  const ref = useRef<any>(null);
  const { width } = useWindowSizes();
  const navigate = useNavigate();
  const likes = useLikedGifs();

  const [likesCount, setLikesCount] = useState(gif.likes);

  const auth = !!currentUser;

  useEffect(() => {
    setLiked(likes.indexOf(gif.id) >= 0);
  }, [gif.id, likes]);

  const handleLike = useCallback(async () => {
    if (auth) {
      if (liked) {
        setLikesCount((prev) => prev - 1);
        setLiked(false);

        apiMain.unlikeGifById({
          gifId: gif.id,
        }).then(() => {
          console.debug(`Gif ${gif.id} unliked.`);
        }).catch((e: any) => {
          console.error(`Gif ${gif.id} could not be unliked: ${e}.`);
          setLiked(true);
        });
      } else {
        setLiked(true);
        setLikesCount((prev) => prev + 1);
        apiMain.likeGifById({
          gifId: gif.id,
        }).then(() => {
          console.debug(`Gif ${gif.id} liked.`);
        }).catch((e: any) => {
          console.error(`Gif ${gif.id} could not be liked: ${e}.`);
          setLiked(false);
        });
        await metrics.sendLiked({ gif: gif.id });
      }
    } else {
      dispatch(showAuthModal());
    }
  }, [auth, dispatch, gif.id, liked]);

  const showPopup = useCallback((event: any) => {
    if (isDesktop) {
      setAnchorEl(event.currentTarget);
    }

    setMenuOpen(prev => {
      if (!prev) {
        metrics.sendOptionClick();
      }

      return !prev;
    });
  }, [isDesktop]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMenuOpen(false);
  }, []);

  const toggleNiche = useCallback(() => {
    console.debug(`[MoreButton] ${isNicheOpen ? "Hiding" : "Showing"} niche popup.`);
    setNicheOpen(!isNicheOpen);
    setMenuOpen(false);
  }, [isNicheOpen]);

  const toggleReport = useCallback(() => {
    console.debug("[MoreButton] Showing report dialog.");
    setMenuOpen(false);
    setReportOpen(!isReportOpen);
  }, [isReportOpen]);

  const toggleCollection = useCallback(() => {
    setIsBookmarkOpen(prev => !prev);
    setMenuOpen(false);
  }, []);

  const toggleShareMenu = useCallback(() => {
    setTimeout(() => {
      setShowShareMenu(prev => {
        if (!prev) {
          metrics.sendShareClick();
        }
        return !prev;
      });
    });
    setMenuOpen(false);
  }, []);

  const toggleChangeNichesDialog = useCallback(() => {
    setShowNichesDialog(true);
    setMenuOpen(false);
  }, []);

  const toggleGifPrefs = useCallback(() => {
    setGifPrefsOpen(prev => !prev);
    setMenuOpen(false);
  }, []);

  const handleCloseNichesDialog = () => {
    setShowNichesDialog(false);
  };

  const toggleQuality = (type: string) => dispatch(setGifQuality(type));
  const toggleSound = () => dispatch(toggleGifSound());

  const renderMoreMenu = useCallback(() => {
    const onManageClick = () => {
      navigate(`/profile/manage?gifId=${props.gif.id}`);
    };

    const onHide = async () => {
      handleClose();

      try {
        await apiMain.hideGif({
          id: props.gif.id,
          home: true,
          trending: true,
        });

        hideGif(props.gif.id);
        console.debug(`Gif ${props.gif.id} hidden successfully.`);
      } catch (e) {
        console.error(`Failed to hide gif ${props.gif.id}.`);
      }
    };

    const actions = {
      onHide,
      onManageClick,
      toggleChangeNichesDialog,
      toggleCollection,
      toggleNiche,
      toggleReport,
      toggleShareMenu,
      toggleGifPrefs
    };
    const moreMenuData = getMoreMenuData({ actions, currentUser, gif: props.gif });
    const menuProps = {
      anchorEl,
      anchorOrigin: {
        vertical: width >= CHANGE_MENU_ALIGN_WIDTH ? "center" : "center",
        horizontal: width >= CHANGE_MENU_ALIGN_WIDTH ? "right" : "left",
      },
      transformOrigin: {
        vertical: width >= CHANGE_MENU_ALIGN_WIDTH ? "center" : "center",
        horizontal: width >= CHANGE_MENU_ALIGN_WIDTH ? "left" : "right",
      }
    };

    return (
      <PopOverMenu
        className="MoreMenu"
        title="Options"
        menuProps={menuProps}
        isOpen={true}
        onClose={handleClose}
        menuItems={moreMenuData}
        isFullscreen={fullscreen}
      />
    );
  }, [anchorEl, currentUser, handleClose, navigate, props.gif, toggleCollection,
    toggleNiche, toggleReport, toggleShareMenu, toggleChangeNichesDialog, width,
    fullscreen, toggleGifPrefs, hideGif
  ]);

  const renderQualityButton = () => {
    if (fullscreen) {
      return null;
    }

    if (gif.type === 1 && quality === "sd") {
      return (
        <button onClick={() => toggleQuality("hd")} className="gif-quality sd">
          <SDIcon />
        </button>
      );
    }

    if (gif.type === 1 && quality === "hd") {
      return (
        <button onClick={() => toggleQuality("sd")} className="gif-quality hd">
          <HDIcon />
        </button>
      );
    }
  };

  return (
    <>
      <div className="sideBar">
        <ViewsButton
          count={gif.views}
          type={gif.type === 1 ? ViewsButtonType.VIDEO : ViewsButtonType.IMAGE}
        />

        {!embedded && <LikeButton
          liked={liked}
          count={likesCount}
          onClick={handleLike}
        />}

        {!embedded && gif.hasAudio && <SoundButton
          enabled={sound}
          onClick={toggleSound}
        />}

        {renderQualityButton()}

        <div className="fs">
          {props.fullscreen ? (
            <SimpleScreenIcon
              onClick={toggleFullScreen}
            />) : (
            <FullScreenIcon
              onClick={toggleFullScreen}
            />
          )}
        </div>

        {!embedded && <div className="more" ref={ref}>
          <button onClick={showPopup} title="Show more actions">
            <MoreRoundIcon />
          </button>
        </div>}

        {!embedded && <div className="live">
          <LiveButton />
        </div>}
      </div>

      {isMenuOpen && renderMoreMenu()}

      {
        showShareMenu && <ShareGifMenu
          gif={gif}
          galleryGifId={galleryGifId}
          anchorEl={anchorEl}
          onClose={toggleShareMenu}
          fullscreen={fullscreen}
        />
      }

      {
        isNicheOpen && (
          <AddToNichePopup
            isOpen={isNicheOpen}
            togglePopup={toggleNiche}
            gif={props.gif}
          />
        )
      }

      {
        isReportOpen && (
          <ReportPopup
            isOpen={isReportOpen}
            togglePopup={toggleReport}
            gifId={props.gif.id}
            handleMainPopup={toggleReport}
          />
        )
      }

      {
        isBookmarkOpen && (
          <AddToCollectionPopup
            isOpen={isBookmarkOpen}
            togglePopup={toggleCollection}
            gifId={props.gif.id}
          />
        )
      }

      {showNichesDialog && (
        <EditNichesDialog
          gifId={props.gif.id}
          onClose={handleCloseNichesDialog}
        />
      )}

      <GifPreferencePopup
        onClose={toggleGifPrefs}
        isOpen={isGifPrefsOpen}
        gifId={props.gif.id}
        sexuality={props.gif?.sexuality || []}
      />
    </>
  );
};

export const SideBarMemo = memo(SideBar);
